import React, { useState } from 'react';
import { brandBand } from '../UniversityCrest';
import { clockOf, dayOf, placesOf, taggedLink } from '../../services/collegeVideos';

// One video card, the ONE card both video surfaces draw (#4728; founder
// 2026-10-18 2:42 PM PT, "we are also showing the shorts in the respective
// university pages"): the college page's Watch section (#4608, pick B) and
// the /videos page. Same poster, same tap: each opens the video where it is
// live, in a new tab, with no player and no request to another site until a
// visitor taps. Each surface passes its own `onPlay(channel, placement)` for
// its own event and its own `medium` for the outbound tags; `footer` is the
// /videos page's link back to the college page.
// Posters are our own stills (scripts/media/video_posters.py), lazy-loaded.

const NEW_TAB = <span className="sr-only"> (opens in a new tab)</span>;

function Poster({ video, href, onClick }) {
    const [broken, setBroken] = useState(false);
    const wide = video.kind === 'tutorial';
    const clock = clockOf(video.duration);
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            data-testid={`college-video-poster-${video.id}`}
            className={`relative block overflow-hidden bg-[#E8E6E0] ${wide ? 'aspect-video' : 'aspect-[9/13]'}`}
        >
            {video.poster && !broken && (
                <img
                    src={video.poster}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width={wide ? 480 : 270}
                    height={wide ? 270 : 480}
                    onError={() => setBroken(true)}
                    className="h-full w-full object-cover object-[center_55%]"
                />
            )}
            <span aria-hidden="true" className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
            </span>
            {clock && (
                <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[11.5px] font-semibold text-white">
                    <span className="sr-only">Length </span>{clock}
                </span>
            )}
            <span className="sr-only">Watch: {video.fact}</span>
            {NEW_TAB}
        </a>
    );
}

/**
 * `college` carries the school's `branding` for the card's colour band;
 * `onPlay(channel, placement)` fires on every outbound tap ('poster' |
 * 'watch' | 'also'); `medium` is the utm_medium of the outbound links.
 */
export default function VideoCard({ college, video, onPlay, medium = 'college_page', footer = null }) {
    const places = placesOf(video);
    if (!places.length) return null;
    const [first, ...rest] = places;
    const click = (channel, placement) => () => { if (onPlay) onPlay(channel, placement); };
    const link = (p) => taggedLink(p.url, { video: video.id, channel: p.key, medium });
    const day = dayOf(video.date);
    return (
        <li data-testid={`college-video-${video.id}`} className="flex w-[204px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-[#E0DED8] bg-white lg:w-auto">
            <span aria-hidden="true" className="block h-1" style={{ background: brandBand(college?.branding) }} />
            <Poster video={video} href={link(first)} onClick={click(first.key, 'poster')} />
            <div className="flex flex-1 flex-col px-3.5 pb-2 pt-3">
                <p className="text-xs text-[#6B6B6B]">
                    {day && <time dateTime={video.date}>{day}</time>}
                    {day && video.topic ? ' · ' : ''}
                    {video.topic}
                </p>
                <p className="mb-2.5 mt-1 flex-1 text-[14.5px] font-semibold leading-snug text-[#1A2E1F]">{video.fact}</p>
                <a
                    href={link(first)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={click(first.key, 'watch')}
                    data-testid={`college-video-watch-${video.id}`}
                    className="flex min-h-[44px] items-center justify-center rounded-lg bg-[#1A4D2E] px-3 text-[13.5px] font-semibold text-white hover:bg-[#245E3A]"
                >
                    Watch on {first.name}{NEW_TAB}
                </a>
                {rest.length > 0 && (
                    <>
                        <p className="mt-2 text-xs text-[#6B6B6B]">Also on</p>
                        <ul className="mt-1 flex flex-wrap gap-1.5">
                            {rest.map((p) => (
                                <li key={p.key}>
                                    <a
                                        href={link(p)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={click(p.key, 'also')}
                                        data-testid={`college-video-${p.key}-${video.id}`}
                                        className="inline-flex min-h-[44px] items-center rounded-full border border-[#CFE0D2] bg-[#F7FBF7] px-2.5 text-[12.5px] font-semibold text-[#1A4D2E] hover:bg-[#EEF5EF]"
                                    >
                                        {p.name}{NEW_TAB}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {footer}
            </div>
        </li>
    );
}
